import { createContext, useContext, useMemo } from 'react';
import useFirebaseAuth from '../hooks/useAuth';
import { Profile } from '../lib/supabase';

// Map Firebase user to profile
const createProfile = (user: any): Profile | null => {
  if (!user) return null;
  
  return {
    id: user.uid,
    email: user.email || '',
    full_name: user.displayName || user.email?.split('@')[0] || 'User',
    avatar_url: user.photoURL || null,
    plan: 'free',
    created_at: new Date().toISOString()
  };
};

type AuthContextType = {
  user: any | null;
  profile: Profile | null;
  loading: boolean;
  isVerified: boolean;
  isAuth: boolean;
  signOut: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const firebaseAuth = useFirebaseAuth();

  const contextValue = useMemo(() => {
    const profile = createProfile(firebaseAuth.currentUser);
    
    return {
      user: firebaseAuth.currentUser,
      profile,
      loading: firebaseAuth.loading,
      isVerified: firebaseAuth.isVerified,
      isAuth: firebaseAuth.isAuth,
      signOut: firebaseAuth.signOut,
      sendVerificationEmail: firebaseAuth.sendVerificationEmail,
    };
  }, [firebaseAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}